import React from "react";
import { useForm, FormProvider } from "react-hook-form";
import { CAutocomplete, CTextField, ProgressButton, useErrorContext } from "shared";
import { useTranslation } from "react-i18next";
import { Box } from "@material-ui/core";
import { useTherapistsList } from "routes/patient/queries/useTherapistsList";
import { usePatientsList } from "routes/therapist/queries/usePatientsList";
import { useExportMessages } from "./useExportMessages";

interface FormData {
  related_id: number;
  from_date?: string;
  to_date?: string;
}

interface Props {
  mode: "patient" | "therapist";
  contextId: number;
  onClose: () => void;
}

export const MessageHistoryForm: React.VFC<Props> = ({ mode, contextId, onClose }) => {
  const { t } = useTranslation();
  const form = useForm<FormData>();
  const setError = useErrorContext();

  // Watch selection to conditionally show download button
  const selectedRelatedId = form.watch("related_id");

  // Load appropriate list based on mode
  const { data: therapists, isLoading: therapistsLoading } = useTherapistsList();
  const { data: patients, isLoading: patientsLoading } = usePatientsList();

  const isPatientMode = mode === "patient";
  const options = isPatientMode ? therapists : patients;
  const optionsLoading = isPatientMode ? therapistsLoading : patientsLoading;
  const label = isPatientMode ? t`Select Therapist` : t`Select Patient`;

  const { mutate: exportMessages, isLoading: exportLoading } = useExportMessages();

  const handleDownload = () => {
    const formData = form.getValues();

    // Date validation
    if (formData.from_date && formData.to_date && formData.from_date > formData.to_date) {
      setError("From date must be before or equal to To date", new Error("Invalid date range"));
      return;
    }

    exportMessages(
      {
        mode,
        contextId,
        relatedId: formData.related_id,
        from_date: formData.from_date,
        to_date: formData.to_date,
      },
      {
        onSuccess: () => {
          onClose();
        },
        onError: (error: any) => {
          setError("Failed to export messages", error);
        },
      }
    );
  };

  return (
    <Box p={3}>
      <FormProvider {...form}>
        <Box mb={2}>
          <CAutocomplete
            name="related_id"
            label={label}
            options={options ?? []}
            loading={optionsLoading}
            getOptionValue={({ id }) => id}
            getOptionLabel={({ first_name, last_name }) => `${first_name} ${last_name}`}
            required
            fullWidth
          />
        </Box>

        <Box mb={2}>
          <CTextField
            name="from_date"
            label={t`From Date (optional)`}
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        <Box mb={3}>
          <CTextField
            name="to_date"
            label={t`To Date (optional)`}
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        {/* Download button only appears after selection */}
        {selectedRelatedId && (
          <ProgressButton loading={exportLoading} onClick={handleDownload} fullWidth>
            {t`Download CSV`}
          </ProgressButton>
        )}
      </FormProvider>
    </Box>
  );
};
